import { useEffect, useState, useCallback } from 'react';
import { Content, Page, Header } from '@backstage/core-components';
import {
  Grid,
  makeStyles,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import { Link, useNavigate } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import {
  shortcutsApiRef,
  type Shortcut,
} from '@backstage-community/plugin-shortcuts';

const useStyles = makeStyles(theme => ({
  container: {
    margin: 0,
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardContent: {
    flexGrow: 1,
    padding: theme.spacing(1, 2),
    '&:last-child': {
      paddingBottom: theme.spacing(1.5),
    },
  },
  button: {
    margin: theme.spacing(0.5),
  },
  shortcutWrapper: {
    position: 'relative',
    '&:hover $deleteBtn': {
      opacity: 1,
    },
  },
  shortcutButton: {
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.type === 'dark' ? '#424242' : '#f5f5f5',
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? '#616161' : '#e0e0e0',
    },
  },
  deleteBtn: {
    position: 'absolute',
    top: -4,
    right: -4,
    opacity: 0,
    transition: 'opacity 0.2s',
    backgroundColor: theme.palette.error.main,
    color: '#fff',
    padding: 2,
    width: 20,
    height: 20,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
    '& svg': {
      fontSize: 14,
    },
  },
  addButton: {
    margin: theme.spacing(0.5),
    borderStyle: 'dashed',
    textTransform: 'none',
  },
}));

// 固定导航项
const FIXED_NAV_ITEMS = [
  { label: '服务目录', to: '/catalog' },
  { label: '技术文档', to: '/docs' },
  { label: '创建组件', to: '/create' },
  { label: '技术雷达', to: '/tech-radar' },
];

export const SimpleHomePage = () => {
  const classes = useStyles();
  const shortcutsApi = useApi(shortcutsApiRef);
  const navigate = useNavigate();

  const [shortcuts, setShortcuts] = useState<Shortcut[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newUrl, setNewUrl] = useState('');

  // 订阅快捷方式变化
  useEffect(() => {
    setShortcuts(shortcutsApi.get());
    const subscription = shortcutsApi.shortcut$().subscribe(value => {
      setShortcuts(value);
    });
    return () => subscription.unsubscribe();
  }, [shortcutsApi]);

  const handleAdd = useCallback(async () => {
    if (!newTitle.trim() || !newUrl.trim()) return;
    await shortcutsApi.add({
      title: newTitle.trim(),
      url: newUrl.trim(),
    });
    setNewTitle('');
    setNewUrl('');
    setDialogOpen(false);
  }, [shortcutsApi, newTitle, newUrl]);

  const handleRemove = useCallback(
    async (id: string) => {
      await shortcutsApi.remove(id);
    },
    [shortcutsApi],
  );

  const handleShortcutClick = useCallback(
    (url: string) => {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(url);
      }
    },
    [navigate],
  );

  return (
    <Page themeId="home">
      <Header title="Internal Developer Platform" subtitle="Accelerating software delivery with modern developer experience" />
      <Content>
        <Grid container spacing={3} className={classes.container}>
          {/* 快速导航 */}
          <Grid item xs={12}>
            <Card className={classes.card}>
              <CardHeader title="快速导航" />
              <CardContent className={classes.cardContent}>
                <Grid container spacing={2}>
                  {/* 固定导航按钮 */}
                  {FIXED_NAV_ITEMS.map(item => (
                    <Grid item xs={6} sm={3} key={item.to}>
                      <Button
                        component={Link}
                        to={item.to}
                        variant="contained"
                        color="primary"
                        fullWidth
                        className={classes.button}
                      >
                        {item.label}
                      </Button>
                    </Grid>
                  ))}

                  {/* 用户自定义快捷方式 */}
                  {shortcuts.map(shortcut => (
                    <Grid item xs={6} sm={3} key={shortcut.id}>
                      <div className={classes.shortcutWrapper}>
                        <Button
                          variant="outlined"
                          fullWidth
                          className={classes.shortcutButton}
                          startIcon={<BookmarkIcon />}
                          onClick={() => handleShortcutClick(shortcut.url)}
                        >
                          {shortcut.title}
                        </Button>
                        <Tooltip title="删除快捷方式">
                          <IconButton
                            className={classes.deleteBtn}
                            size="small"
                            onClick={() => handleRemove(shortcut.id)}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </Grid>
                  ))}

                  {/* 添加快捷方式按钮 - 永远在最后 */}
                  <Grid item xs={6} sm={3}>
                    <Button
                      variant="outlined"
                      color="default"
                      fullWidth
                      className={classes.addButton}
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setNewUrl(window.location.pathname);
                        setDialogOpen(true);
                      }}
                    >
                      Add Shortcuts
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* 欢迎信息 */}
          <Grid item xs={12} md={6}>
            <Card className={classes.card}>
              <CardHeader title="开始使用" />
              <CardContent className={classes.cardContent}>
                <Typography variant="body1" paragraph>
                  欢迎使用我们的开发者门户！这里您可以：
                </Typography>
                <Typography variant="body2" component="ul">
                  <li>浏览和搜索所有服务和组件</li>
                  <li>查看详细的技术文档</li>
                  <li>创建新的组件和服务</li>
                  <li>查看技术选型雷达</li>
                  <li>管理 Kubernetes 资源</li>
                  <li>监控 ArgoCD 部署状态</li>
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* 链接资源 */}
          <Grid item xs={12} md={6}>
            <Card className={classes.card}>
              <CardHeader title="有用链接" />
              <CardContent className={classes.cardContent}>
                <Typography variant="body2" component="div">
                  <p><strong>内部工具:</strong></p>
                  <ul>
                    <li><a href="/search">搜索</a> - 搜索所有实体</li>
                    <li><a href="/settings">设置</a> - 个人设置</li>
                    <li><a href="/catalog-graph">关系图</a> - 组件关系可视化</li>
                  </ul>
                  <p><strong>外部资源:</strong></p>
                  <ul>
                    <li><a href="https://backstage.io/docs" target="_blank" rel="noopener noreferrer">Backstage 文档</a></li>
                    <li><a href="https://kubernetes.io/docs" target="_blank" rel="noopener noreferrer">Kubernetes 文档</a></li>
                  </ul>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 添加快捷方式对话框 */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>添加快捷方式</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="名称"
              placeholder="例如：我的服务"
              fullWidth
              variant="outlined"
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
            />
            <TextField
              margin="dense"
              label="URL"
              placeholder="例如：/catalog/default/component/my-service"
              fullWidth
              variant="outlined"
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
              helperText="支持站内路径和外部链接"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>取消</Button>
            <Button
              onClick={handleAdd}
              color="primary"
              variant="contained"
              disabled={!newTitle.trim() || !newUrl.trim()}
            >
              添加
            </Button>
          </DialogActions>
        </Dialog>
      </Content>
    </Page>
  );
};
